<template>
  <div class="kp-dlp">
    <p class="kp-dlp__meta">
      {{ headerMeta }}
    </p>

    <div class="kp-dlp__scroll">
      <!-- Collection export (the XLSX): always available, and selectable like any other item -->
      <label
        class="kp-dlp__order"
        :class="{ 'kp-dlp__order--on': orderFormChecked, 'kp-dlp__order--alone': !hasFiles }"
      >
        <!-- Ticking only matters when there is something to combine with. -->
        <template v-if="hasFiles">
          <input
            v-model="orderFormChecked"
            type="checkbox"
            class="kp-dlp__checkbox"
          >
          <span
            class="kp-dlp__box"
            :class="{ 'kp-dlp__box--on': orderFormChecked }"
            aria-hidden="true"
          >
            <svg
              width="9"
              height="7"
              viewBox="0 0 10 8"
            >
              <path
                d="M1 4 3.8 6.8 9 1"
                stroke="#fff"
                stroke-width="1.8"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </template>
        <span class="kp-dlp__tag kp-dlp__tag--accent">XLSX</span>
        <span class="kp-dlp__order-main">
          <span class="kp-dlp__order-title">Collection</span>
          <span class="kp-dlp__order-sub">Generated from the latest products</span>
        </span>
        <button
          type="button"
          class="kp-dlp__row-btn kp-dlp__row-btn--raised"
          aria-label="Download collection"
          :disabled="busy"
          @click.prevent.stop="downloadOrderForm"
        >
          <span
            v-if="pending === 'orderForm'"
            class="kp-dlp__spinner"
            aria-hidden="true"
          />
          <svg
            v-else-if="doneKey === 'orderForm'"
            class="kp-dlp__tick"
            width="13"
            height="11"
            viewBox="0 0 10 8"
            aria-hidden="true"
          >
            <path
              d="M1 4 3.8 6.8 9 1"
              stroke="currentColor"
              stroke-width="1.8"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            width="14"
            height="14"
            viewBox="0 0 16 16"
            aria-hidden="true"
          >
            <path
              d="M8 2.5v7M4.8 6.7 8 9.9l3.2-3.2M3.2 13h9.6"
              stroke="currentColor"
              stroke-width="1.6"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </label>

      <!-- One ledger section per populated file group -->
      <section
        v-for="section in fileSections"
        :key="section.key"
        class="kp-dlp__section"
      >
        <div class="kp-dlp__sec-head">
          <button
            type="button"
            class="kp-dlp__acc-head"
            :aria-expanded="isExpanded(section.key) ? 'true' : 'false'"
            @click="toggleExpand(section.key)"
          >
            <span class="kp-dlp__sec-title">{{ section.title }}</span>
            <span class="kp-dlp__sec-meta">{{ sectionMeta(section) }}</span>
          </button>
          <button
            v-if="isExpanded(section.key)"
            type="button"
            class="kp-dlp__link"
            @click="toggleSectionAll(section)"
          >
            {{ allCheckedIn(section) ? 'Clear' : 'All' }}
          </button>
          <button
            type="button"
            class="kp-dlp__chev-btn"
            :aria-label="`${isExpanded(section.key) ? 'Collapse' : 'Expand'} ${section.title}`"
            @click="toggleExpand(section.key)"
          >
            <svg
              class="kp-dlp__chevron"
              :class="{ 'kp-dlp__chevron--open': isExpanded(section.key) }"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              aria-hidden="true"
            >
              <path
                d="M2.5 4.5 6 8l3.5-3.5"
                stroke="currentColor"
                stroke-width="1.5"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div
          class="kp-dlp__acc-body"
          :class="{ 'kp-dlp__acc-body--open': isExpanded(section.key) }"
        >
          <div class="kp-dlp__acc-inner">
            <label
              v-for="row in section.rows"
              :key="row.key"
              class="kp-dlp__row"
              :class="{ 'kp-dlp__row--on': isChecked(row.key) }"
            >
              <input
                type="checkbox"
                class="kp-dlp__checkbox"
                :checked="isChecked(row.key)"
                @change="toggleCheck(row.key)"
              >
              <span
                class="kp-dlp__box"
                :class="{ 'kp-dlp__box--on': isChecked(row.key) }"
                aria-hidden="true"
              >
                <svg
                  width="9"
                  height="7"
                  viewBox="0 0 10 8"
                >
                  <path
                    d="M1 4 3.8 6.8 9 1"
                    stroke="#fff"
                    stroke-width="1.8"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="kp-dlp__tag">{{ row.ext }}</span>
              <span class="kp-dlp__row-name">{{ row.name }}</span>
              <span
                v-if="row.size"
                class="kp-dlp__row-size"
              >{{ formatBytes(row.size) }}</span>
              <button
                type="button"
                class="kp-dlp__row-btn kp-dlp__row-btn--icon"
                :aria-label="`Download ${row.name}`"
                :disabled="busy"
                @click.prevent.stop="downloadRow(row)"
              >
                <span
                  v-if="pending === row.key"
                  class="kp-dlp__spinner"
                  aria-hidden="true"
                />
                <svg
                  v-else-if="doneKey === row.key"
                  class="kp-dlp__tick"
                  width="13"
                  height="11"
                  viewBox="0 0 10 8"
                  aria-hidden="true"
                >
                  <path
                    d="M1 4 3.8 6.8 9 1"
                    stroke="currentColor"
                    stroke-width="1.8"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  <path
                    d="M8 2.5v7M4.8 6.7 8 9.9l3.2-3.2M3.2 13h9.6"
                    stroke="currentColor"
                    stroke-width="1.6"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </label>
          </div>
        </div>
      </section>

    </div>

    <!-- One adaptive action: nothing picked means everything ships -->
    <div
      v-if="hasFiles"
      class="kp-dlp__footer"
    >
      <button
        type="button"
        class="kp-dlp__everything"
        :disabled="busy"
        @click="downloadCta"
      >
        <span
          v-if="ctaPending"
          class="kp-dlp__spinner kp-dlp__spinner--light"
          aria-hidden="true"
        />
        <svg
          v-else
          width="15"
          height="15"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path
            d="M8 2.5v7M4.8 6.7 8 9.9l3.2-3.2M3.2 13h9.6"
            stroke="currentColor"
            stroke-width="1.6"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="kp-dlp__everything-label">{{ ctaLabel }}</span>
      </button>
      <div class="kp-dlp__cta-sub">
        {{ ctaSub }}
      </div>
    </div>
  </div>
</template>

<script>
// KP-3919: ONE download experience for every Download Collection surface.
// Hosts supply the collection and dispatch; this component owns what can be
// picked and how each action maps to the download API:
//   order-form button -> deliverable orderForm
//   one file row      -> deliverable selection, that file (original format)
//   footer action     -> nothing (or everything) ticked: deliverable all;
//                        a subset: deliverable selection of the ticked items
import {
  getCollectionDownloadFiles,
  DOWNLOAD_DELIVERABLE,
  DOWNLOAD_ASSET_SECTIONS,
  formatBytes,
} from '@/common-utils'
import { useClientZipDownload } from '@/composables/useClientZipDownload'
import { apiToastSuccess } from '@/@core/utils/toast'

const extensionOf = name => {
  const dot = (name || '').lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'FILE'
}

export default {
  name: 'CollectionDownloadPicker',
  props: {
    collection: { type: Object, default: () => null },
    collectionId: { type: String, default: null },
    retailerId: { type: String, default: null },
    downloading: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    return useClientZipDownload({
      collectionId: () => props.collectionId
        || (props.collection && props.collection._id)
        || null,
      retailerId: () => props.retailerId,
      zipName: () => props.collection && props.collection.name,
      // The download is underway — the popup's job is done. The TAB must stay
      // open (the page feeds the stream), the popup needn't.
      onStarted: () => {
        apiToastSuccess('Download started — the ZIP is building on your device. Keep this tab open until it finishes.')
        emit('close-popup')
      },
    })
  },
  data() {
    return {
      DELIVERABLE: DOWNLOAD_DELIVERABLE,
      formatBytes,
      // Sections open by default; this tracks the ones the user folded away.
      collapsedKeys: [],
      checkedKeys: [],
      // Not rendered, so it needs no reactivity — declared for the same
      // explicitness as the rest of this block.
      doneTimer: null,
      doneKey: null,
      orderFormChecked: false,
      pending: null,
    }
  },
  computed: {
    files() {
      return getCollectionDownloadFiles(this.collection)
    },
    fileSections() {
      return [
        {
          key: DOWNLOAD_ASSET_SECTIONS.BRAND_CATALOG,
          title: 'Brand catalogue',
          rows: this.sectionRows(DOWNLOAD_ASSET_SECTIONS.BRAND_CATALOG, this.files.brandCatalog),
        },
        {
          key: DOWNLOAD_ASSET_SECTIONS.ADDITIONAL,
          title: 'Additional files',
          rows: this.sectionRows(DOWNLOAD_ASSET_SECTIONS.ADDITIONAL, this.files.additional),
        },
      ].filter(section => section.rows.length)
    },
    hasFiles() {
      return this.fileSections.length > 0
    },
    allRows() {
      return this.fileSections.reduce((rows, section) => [...rows, ...section.rows], [])
    },
    totalFiles() {
      return this.allRows.length
    },
    totalBytes() {
      return this.allRows.reduce((sum, row) => sum + (row.size || 0), 0)
    },
    checkedRows() {
      return this.allRows.filter(row => this.isChecked(row.key))
    },
    checkedBytes() {
      return this.checkedRows.reduce((sum, row) => sum + (row.size || 0), 0)
    },
    // Nothing ticked is the same promise as everything ticked: the whole
    // collection. That is what makes the single action safe to lead with.
    isEverything() {
      const nothingPicked = !this.orderFormChecked && this.checkedRows.length === 0
      return nothingPicked
        || (this.orderFormChecked && this.checkedRows.length === this.totalFiles)
    },
    pickedCount() {
      return this.checkedRows.length + (this.orderFormChecked ? 1 : 0)
    },
    headerMeta() {
      const items = this.totalFiles + 1
      const size = formatBytes(this.totalBytes)
      return `${items} ${items === 1 ? 'item' : 'items'}${size ? ` · ${size} total` : ''}`
    },
    ctaPending() {
      return this.pending === 'cta' || this.clientZip.running
    },
    ctaLabel() {
      if (this.clientZip.running) return 'Building ZIP on your device…'
      if (this.pending === 'cta') return 'Preparing ZIP…'
      if (this.isEverything) return 'Download complete collection'
      return `Download ${this.pickedCount} ${this.pickedCount > 1 ? 'files' : 'file'}`
    },
    ctaSub() {
      if (this.clientZip.running) {
        // Before the first chunk lands there is nothing to count, and the
        // fallback has to be a whole phrase — "Starting written" is not one.
        const written = formatBytes(this.clientZip.loadedBytes)
        return `${written ? `${written} written` : 'Starting'} — keep this tab open`
      }
      if (this.isEverything) {
        const size = formatBytes(this.totalBytes)
        return `Collection + all ${this.totalFiles} files${size ? ` · ${size}` : ''} · one ZIP`
      }
      const size = formatBytes(this.checkedBytes)
      const shape = this.pickedCount > 1 ? 'one ZIP' : 'original format'
      return `${size ? `${size} · ` : ''}${shape}`
    },
    resolvedCollectionId() {
      return this.collectionId || (this.collection && this.collection._id) || null
    },
    busy() {
      return this.downloading || this.clientZip.running
    },
  },
  watch: {
    // A finished dispatch clears the in-flight marker and leaves a tick on
    // the control that was used, so a single download confirms itself.
    downloading(value) {
      if (value) return
      if (this.pending && this.pending !== 'cta') this.flashDone(this.pending)
      this.pending = null
    },
    // New collection — reset transient UI state.
    collection() {
      this.collapsedKeys = []
      this.checkedKeys = []
      this.orderFormChecked = false
      this.clearDone()
    },
  },
  beforeDestroy() {
    this.clearDone()
  },
  methods: {
    flashDone(key) {
      this.clearDone()
      this.doneKey = key
      this.doneTimer = setTimeout(() => { this.doneKey = null }, 1600)
    },
    clearDone() {
      clearTimeout(this.doneTimer)
      this.doneKey = null
    },
    sectionRows(section, files) {
      return files.map((file, index) => ({
        key: `${section}:${index}`,
        section,
        name: file.name,
        size: file.size || 0,
        ext: extensionOf(file.name),
      }))
    },
    sectionMeta(section) {
      const bytes = section.rows.reduce((sum, row) => sum + (row.size || 0), 0)
      const size = formatBytes(bytes)
      return `${section.rows.length} ${section.rows.length === 1 ? 'file' : 'files'}${size ? ` · ${size}` : ''}`
    },
    isExpanded(key) {
      return !this.collapsedKeys.includes(key)
    },
    toggleExpand(key) {
      this.collapsedKeys = this.isExpanded(key)
        ? [...this.collapsedKeys, key]
        : this.collapsedKeys.filter(k => k !== key)
    },
    isChecked(key) {
      return this.checkedKeys.includes(key)
    },
    toggleCheck(key) {
      this.checkedKeys = this.isChecked(key)
        ? this.checkedKeys.filter(k => k !== key)
        : [...this.checkedKeys, key]
    },
    checkedRowsIn(section) {
      return section.rows.filter(row => this.isChecked(row.key))
    },
    allCheckedIn(section) {
      return section.rows.every(row => this.isChecked(row.key))
    },
    toggleSectionAll(section) {
      const keys = section.rows.map(row => row.key)
      const others = this.checkedKeys.filter(key => !keys.includes(key))
      this.checkedKeys = this.allCheckedIn(section) ? others : [...others, ...keys]
    },
    selectionPayload(rows, withOrderForm) {
      return {
        deliverable: DOWNLOAD_DELIVERABLE.SELECTION,
        selectedAssets: [
          ...(withOrderForm ? [{ section: DOWNLOAD_ASSET_SECTIONS.ORDER_FORM }] : []),
          ...rows.map(row => ({ section: row.section, name: row.name })),
        ],
      }
    },
    downloadRow(row) {
      this.request(row.key, this.selectionPayload([row]))
    },
    downloadOrderForm() {
      const started = this.request('orderForm', { deliverable: DOWNLOAD_DELIVERABLE.ORDER_FORM })
      // With no catalogue or additional files the spreadsheet IS the whole
      // collection, so it ends the session like the main action does. With
      // files alongside, the popup stays open for more picks.
      if (started && !this.hasFiles) this.$emit('close-popup')
    },
    request(pendingKey, payload) {
      if (this.busy) return false
      this.pending = pendingKey
      this.$emit('download', payload)
      return true
    },
    // KP-3919 large tier: above the threshold the browser builds the zip
    // itself — no server bytes, no stored artifact, any size. Must run
    // synchronously from the click so the save dialog keeps the gesture.
    downloadCta() {
      if (this.busy) return
      if (this.isEverything) {
        this.dispatchDownload({
          bytes: this.totalBytes,
          payload: { deliverable: DOWNLOAD_DELIVERABLE.ALL },
        })
        return
      }
      const rows = this.checkedRows
      this.dispatchDownload({
        bytes: this.checkedBytes,
        payload: this.selectionPayload(rows, this.orderFormChecked),
      })
      // The batch is on its way — reset the picks so the next one starts clean.
      this.checkedKeys = []
      this.orderFormChecked = false
    },
    dispatchDownload({ bytes, payload }) {
      if (this.shouldClientZip(bytes)) {
        this.runClientZip({
          deliverable: payload.deliverable,
          selectedAssets: payload.selectedAssets,
          fallback: () => this.request('cta', payload),
        })
        return
      }
      this.warnIfDegraded(bytes)
      this.request('cta', payload)
      // A whole-collection or multi-select download ends the session — the
      // work continues in the Downloads tray, so get out of the way.
      this.$emit('close-popup')
    },
  },
}
</script>

<style lang="scss" scoped>
// KP-3919 "Ledger" — the approved download popup: a quiet editorial list
// where every item is a line, sections are rules rather than boxes, and one
// adaptive action carries the whole collection. Colours come from the v2
// tokens with literal fallbacks so the legacy bootstrap modals match too.
.kp-dlp {
  $border: var(--v2-border, #e9e7f2);
  $rule: rgba(116, 125, 238, 0.13);
  $ink: var(--v2-foreground, #17163a);
  $ink-soft: #2a2947;
  $muted: #8987a3;
  $faint: #a5a4bd;
  $indigo: var(--v2-primary, #6a73e8);
  $indigo-deep: #4f56d6;
  $tint: var(--v2-muted, #f4f5fe);
  $chip: #f2f2f8;
  $focus-ring: rgba(116, 125, 238, 0.55);
  $gutter: 12px;

  display: flex;
  flex-direction: column;
  text-align: left;
  font-family: var(--v2-font-sans);
  animation: kp-dlp-pop 0.45s cubic-bezier(0.2, 0.8, 0.2, 1) both;

  &__meta {
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
    color: $muted;
    margin: 0 0 10px;
    padding: 0 $gutter;
  }

  // The popup grows with the collection: the list takes whatever height the
  // screen has left once the modal chrome, the summary and the action are
  // accounted for, and only scrolls past that. Short collections keep their
  // natural height and never show a scrollbar.
  &__scroll {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
    overscroll-behavior: contain;
    // Room for the scrollbar so it never sits on top of the sizes.
    padding-right: 2px;
    margin-right: -2px;

    // Below this the subtraction leaves too little to be useful; hold a
    // floor and let the list scroll sooner instead.
    @media (max-height: 540px) { max-height: 240px; }

    &::-webkit-scrollbar { width: 6px; }

    &::-webkit-scrollbar-thumb {
      background: rgba(116, 125, 238, 0.28);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover { background: rgba(116, 125, 238, 0.45); }

    &::-webkit-scrollbar-track { background: transparent; }
  }

  // Shared: the drawn tick box that stands in for the native checkbox.
  &__checkbox {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }

  &__box {
    width: 16px;
    height: 16px;
    border-radius: 5px;
    display: grid;
    place-items: center;
    flex: none;
    border: 1.5px solid #c9c8dd;
    background: #fff;
    transition: border-color 0.15s ease, background 0.15s ease;

    svg {
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &--on {
      border-color: $indigo;
      background: $indigo;

      svg { opacity: 1; }
    }
  }

  &__checkbox:focus-visible + &__box {
    outline: 2px solid $focus-ring;
    outline-offset: 2px;
  }

  // Format tag — mono, quiet, fixed rhythm with the name that follows.
  &__tag {
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    line-height: 1;
    color: $muted;
    background: $chip;
    border-radius: 5px;
    padding: 4px 6px;
    flex: none;

    &--accent {
      color: $indigo-deep;
      background: rgba(116, 125, 238, 0.14);
    }
  }

  // Collection export — the one item that always exists, given a tinted plinth.
  &__order {
    display: flex;
    align-items: center;
    gap: 10px;
    background: $tint;
    border-radius: 10px;
    padding: 8px $gutter;
    margin: 0;
    cursor: pointer;
    transition: box-shadow 0.15s ease;

    &--on { background: rgba(116, 125, 238, 0.12); }

    // The only item in the popup — nothing to tick it against.
    &--alone { cursor: default; }

    &-main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    &-title {
      font-size: 0.84375rem;
      font-weight: 600;
      line-height: 1.3;
      color: $ink-soft;
    }

    &-sub {
      font-size: 0.6875rem;
      line-height: 1.3;
      color: $muted;
    }
  }

  // Section — a rule, a label, a count. No box, no card.
  &__section {
    border-top: 1px solid $rule;
    margin-top: 6px;

    &:first-of-type { margin-top: 10px; }
  }

  &__sec-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 $gutter;
  }

  &__acc-head {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 0 9px;
    background: none;
    border: 0;
    cursor: pointer;
    text-align: left;

    &:focus-visible {
      outline: 2px solid $focus-ring;
      outline-offset: 2px;
      border-radius: 6px;
    }
  }

  &__sec-title {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #55546e;
  }

  &__sec-meta {
    font-size: 0.6875rem;
    font-variant-numeric: tabular-nums;
    color: $faint;
  }

  // The chevron keeps its own hit area so the rule reads label → count →
  // (all) → chevron, left to right, with the disclosure always last.
  &__chev-btn {
    flex: none;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 6px;
    background: none;
    color: $muted;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: $chip; }

    &:focus-visible {
      outline: 2px solid $focus-ring;
      outline-offset: 2px;
    }
  }

  &__chevron {
    transition: transform 0.3s ease;

    &--open { transform: rotate(180deg); }
  }

  &__link {
    flex: none;
    border: 0;
    background: none;
    padding: 4px 2px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $indigo-deep;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.7; }

    &:focus-visible {
      outline: 2px solid $focus-ring;
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  // Grid-rows collapse: height animates without measuring anything.
  &__acc-body {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    &--open { grid-template-rows: 1fr; }
  }

  &__acc-inner {
    overflow: hidden;
    min-height: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px $gutter;
    margin: 0 0 1px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: rgba(116, 125, 238, 0.055); }

    &--on,
    &--on:hover { background: rgba(116, 125, 238, 0.12); }

    &:last-child { margin-bottom: 8px; }
  }

  &__row-name {
    flex: 1;
    min-width: 0;
    font-size: 0.84375rem;
    color: $ink-soft;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__row-size {
    font-size: 0.6875rem;
    font-variant-numeric: tabular-nums;
    color: $faint;
    flex: none;
  }

  // Per-item download — ghost by default, raised on the order form.
  &__row-btn {
    flex: none;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 7px;
    background: none;
    color: #6f6e87;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
      background: $chip;
      color: $indigo-deep;
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid $focus-ring;
      outline-offset: 2px;
    }

    &--raised {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: #fff;
      color: $indigo-deep;
      box-shadow: 0 1px 3px rgba(23, 22, 58, 0.12);

      &:hover:not(:disabled) {
        background: #fff;
        box-shadow: 0 2px 6px rgba(23, 22, 58, 0.2);
      }
    }
  }

  &__tick {
    color: #1fa06a;
    animation: kp-dlp-tick 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  }

  &__spinner {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    border: 2px solid rgba(116, 125, 238, 0.28);
    border-top-color: $indigo;
    animation: kp-dlp-spin 0.7s linear infinite;

    &--light {
      border-color: rgba(255, 255, 255, 0.4);
      border-top-color: #fff;
    }
  }

  // The single action. Its label is the promise; the line under it is the
  // shape of what arrives.
  &__footer {
    border-top: 1px solid $rule;
    padding-top: 14px;
    margin-top: 10px;
  }

  &__everything {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 0;
    border-radius: 12px;
    padding: 13px 18px;
    background: $indigo;
    color: #fff;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;

    &:hover:not(:disabled) {
      background: $indigo-deep;
      box-shadow: 0 4px 14px rgba(116, 125, 238, 0.35);
    }

    &:active:not(:disabled) { transform: scale(0.985); }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &:focus-visible {
      outline: 2px solid $focus-ring;
      outline-offset: 2px;
    }

    &-label {
      font-size: 0.875rem;
      font-weight: 600;
    }
  }

  &__cta-sub {
    text-align: center;
    font-size: 0.71875rem;
    color: $faint;
    margin-top: 9px;
  }
}

@keyframes kp-dlp-pop {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes kp-dlp-spin {
  to { transform: rotate(360deg); }
}

@keyframes kp-dlp-tick {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kp-dlp {
    animation: none;

    &__acc-body,
    &__chevron,
    &__row,
    &__box,
    &__everything {
      transition: none;
    }

    &__tick { animation: none; }

    &__everything:active:not(:disabled) { transform: none; }
  }
}
</style>
